import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import { initRum } from './observability/rum';

initRum();

const envMode = process.env.REACT_APP_ENV;
const currentPath = window.location.pathname;

const excludePaths = [
    '/room/recording/',
    '/room/feedback/',
    '/dashboard',
    '/interviews',
    '/profile',
    '/billings',
    '/plans',
    '/settings',
    '/support',
    '/logout',
];

const shouldEnableSentry =
    ['prod', 'test', 'dev'].includes(envMode) && !excludePaths.some((path) => currentPath.startsWith(path));

let sentryInitialized = false;
let lastRestartAt = 0;

function restartSentrySession(reason) {
    if (!sentryInitialized) return;

    const now = Date.now();

    // Prevent restart spam (5s debounce)
    if (now - lastRestartAt < 5000) return;
    lastRestartAt = now;

    try {
        Sentry.endSession();
        Sentry.startSession();

        Sentry.setContext('session_restart', {
            reason: reason,
            timestamp: new Date().toISOString(),
        });

        Sentry.flush(5000);
    } catch (e) {
        console.warn('[Sentry] Failed to restart session', e);
    }
}

if (shouldEnableSentry) {
    Sentry.init({
        dsn: 'https://bd5bd23f7b22113afb8e0d75a6ee8d68@o4506863228616704.ingest.us.sentry.io/4508850384076800',

        release: process.env.REACT_APP_VERSION,
        environment: envMode,

        autoSessionTracking: true,

        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
                networkDetailAllowUrls: [
                    /^https:\/\/dev\.api\.incruiter\.com/,
                    /^https:\/\/test\.api\.incruiter\.com/,
                    /^https:\/\/api\.incruiter\.com/,
                    /^https:\/\/.*\.incruiter\.com/,
                ],
                networkRequestHeaders: ['Authorization', 'Content-Type'],
                networkResponseHeaders: ['Content-Type'],
            }),
            Sentry.replayCanvasIntegration(),
        ],

        transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

        tracesSampleRate: envMode === 'prod' ? 0.1 : 1.0,
        replaysSessionSampleRate: envMode === 'prod' ? 0.1 : 1.0,
        replaysOnErrorSampleRate: 1.0,

        tracePropagationTargets: ['localhost', /^https:\/\/.*\.incruiter\.com/],
        enableLogs: true,
    });

    sentryInitialized = true;
    Sentry.setTag('env', envMode);

    window.addEventListener('online', function () {
        restartSentrySession('network_online');
    });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            restartSentrySession('tab_visible');
        }
    });
}

const root = createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
);
